<script setup lang="ts">
import { computed } from 'vue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@pkg/ui/components/ui/dialog';
import { BaseButtonGroup } from '@pkg/ui/components/base/BaseButtonGroup';
import UiButton from '@pkg/ui/components/ui/button/ButtonUi.vue';
import type { PantryButtonConfig } from '@pkg/ui/components/ui/button';
import { AppStore as AppStoreType } from '@pkg/sanity-utils/constants/appstore';
import { useAppStoresConfig } from '../../../frontend/appStores';
import AppStoreBadge from './AppStore.vue';

const appStores = useAppStoresConfig();

const { userAgent, isIos, isAndroid } = useDevice();

interface Props {
  config?: PantryButtonConfig;
}

const props = defineProps<Props>();

const appStoreLink = computed(() => {
  const stores = appStores.value;
  if (!stores) return undefined;

  if (isIos) {
    return stores.appleAppStore ?? undefined;
  }
  if (isAndroid) {
    // Basic Huawei HMS heuristic — detecting HMS APIs is more complex.
    if (/huawei/i.test(userAgent)) {
      return stores.huaweiAppGallery ?? undefined;
    }
    return stores.googlePlayStore ?? undefined;
  }
  return undefined;
});

const buttons = [
  AppStoreType.APPLE_APP_STORE,
  AppStoreType.GOOGLE_PLAY_STORE,
  AppStoreType.HUAWEI_APP_GALLERY,
] as const;

const modalTitle = computed(() => appStores.value?.modalTitleAppStoreDownload);
</script>

<template>
  <NuxtLink v-if="appStoreLink" :to="appStoreLink">
    <UiButton :config="props.config">
      <slot />
    </UiButton>
  </NuxtLink>
  <div v-else>
    <Dialog>
      <DialogTrigger as-child>
        <UiButton :config="props.config">
          <slot />
        </UiButton>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <div class="text-left">
            <DialogTitle>
              <h2 class="text-h3 uppercase text-primary-30">
                {{ modalTitle }}
              </h2>
            </DialogTitle>
            <DialogDescription class="sr-only">
              Download using the links below
            </DialogDescription>
          </div>
        </DialogHeader>
        <BaseButtonGroup style="--button-group-gap: 0.75rem">
          <AppStoreBadge
            v-for="button in buttons"
            :key="button"
            :app-store="button"
            button-color="white"
          />
        </BaseButtonGroup>
      </DialogContent>
    </Dialog>
  </div>
</template>
